"use client";

import { EventJourneyStatus, Prisma } from "@prisma/client";
import { useRef, useState } from "react";
import { api } from "~/utils/api";
import { FiAlertCircle } from "react-icons/fi";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../v3/shadcn/Card";
import { toast } from "react-toastify";
import { cn } from "~/utils/fontsConfig";
import { Alert, AlertDescription, AlertTitle } from "../shadcn/Alert";
import { Label } from "../v3/shadcn/Label";
import { Switch } from "../v3/themed/Switch";
import { Button } from "../v3/themed/Button";
import { TextArea } from "../v3/themed/TextArea";
import FacetedFilterSelector from "~/app/_components/FacetedFilterSelector";
import { revalidateEventFallback } from "~/app/events/[slug]/actions";

interface Props {
    event: Prisma.EventGetPayload<{
        select: {
            id: true;
            slug: true;
            eventJourneyStatus: true;
            attendees: {
                select: { status: true };
            };
            communityId: true;
            community: {
                select: {
                    isHostAllowedToSMS: true;
                    isHostAllowedToEmail: true;
                };
            };
        };
    }>;
    hasPermissions: boolean;
    className?: string;
}

const AdHocMessengerManager = ({ event, hasPermissions, className }: Props) => {
    const selectorRef = useRef<{ clearSelections: () => void }>(null);
    const [message, setMessage] = useState("");
    const [sendSms, setSendSms] = useState<boolean>(false);
    const [sendEmail, setSendEmail] = useState<boolean>(false);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const uniqueStatuses = [...new Set(event.attendees.map((a) => a.status))];
    const options = uniqueStatuses.map((status) => ({
        value: status,
        count: event.attendees.filter((a) => a.status === status).length,
    }));
    const [statusOptions, setStatusOptions] =
        useState<{ value: string; count: number }[]>(options);

    const getSmsAndEmailPermissions = () => {
        if (hasPermissions)
            return {
                canSendSms: true,
                canSendEmail: true,
            };

        return {
            canSendSms: event?.community?.isHostAllowedToSMS,
            canSendEmail: event?.community?.isHostAllowedToEmail,
        };
    };

    const { canSendSms, canSendEmail } = getSmsAndEmailPermissions();
    const sendAdHocNotification =
        api.message.sendAdHocEventNotificationToEventAttendees.useMutation();

    const handleSendAdHocNotification = async () => {
        if (!selectedStatuses.length) return;

        toast.loading(`Sending messages to selected attendees...`, {
            toastId: `sending-messages`,
        });

        await sendAdHocNotification.mutateAsync(
            {
                sendSms,
                sendEmail,
                recipientStatuses: selectedStatuses,
                content: message,
                eventId: event.id,
                eventSlug: event.slug,
                ...(event.communityId
                    ? { communityId: event.communityId }
                    : {}),
            },
            {
                onSuccess: () => {
                    toast.dismiss(`sending-messages`);
                    toast.success(`Messages sent successfully!`);
                    setMessage("");
                    setSendEmail(false);
                    setSendSms(false);
                    setSelectedStatuses([]);
                    selectorRef.current?.clearSelections();
                    revalidateEventFallback(event.slug);
                },
                onError: (err) => {
                    toast.dismiss(`sending-messages`);
                    toast.error(`Error sending messages: ${err.message}`);
                },
            },
        );
    };

    return (
        <Card className="flex flex-col gap-3 p-0">
            <CardHeader className="flex flex-col gap-2 p-0">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">
                        Send Message Blast
                    </CardTitle>
                    <FacetedFilterSelector
                        ref={selectorRef}
                        placeholder="Add recipients"
                        onFilterChange={() => {}}
                        onSelectonChange={setSelectedStatuses}
                        options={statusOptions}
                        className="ml-auto"
                    />
                </div>
                <CardDescription className="text-v3-N-300">
                    Send a message blast to event attendees. Please note sms
                    messages are one-way; attendees can respond via email
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 p-0">
                {event.eventJourneyStatus !== EventJourneyStatus.PUBLISHED && (
                    <Alert variant="destructive" className="bg-background">
                        <FiAlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>
                            <p>
                                The event is not published yet. You will not be
                                able to send messages until the event status is
                                published
                            </p>
                        </AlertDescription>
                    </Alert>
                )}

                <div
                    className={cn(
                        "space-y-5",
                        event.eventJourneyStatus !==
                            EventJourneyStatus.PUBLISHED && "blur",
                    )}
                >
                    <div className="space-y-1">
                        <Label htmlFor="message">Message</Label>
                        <TextArea
                            id="message"
                            className="h-20 placeholder:not-italic"
                            placeholder="Event update!"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center justify-between space-x-2">
                        <Label
                            htmlFor="sms-switch"
                            className="flex flex-col space-y-1"
                        >
                            <span>SMS</span>
                            <span className="font-normal leading-snug text-muted-foreground">
                                Send a mobile sms blast to attendees.
                            </span>
                        </Label>
                        <Switch
                            id="sms-switch"
                            defaultChecked
                            checked={sendSms}
                            onCheckedChange={(val) => setSendSms(val)}
                            disabled={!canSendSms}
                        />
                    </div>
                    <div className="flex items-center justify-between space-x-2">
                        <Label
                            htmlFor="email-switch"
                            className="flex flex-col space-y-1"
                        >
                            <span>Email</span>
                            <span className="font-normal leading-snug text-muted-foreground">
                                Send an email blast to attendees.
                            </span>
                        </Label>
                        <Switch
                            id="email-switch"
                            checked={sendEmail}
                            onCheckedChange={(val) => setSendEmail(val)}
                            disabled={!canSendEmail}
                        />
                    </div>
                </div>
            </CardContent>
            <CardFooter className="p-0">
                <Button
                    className="text-primary"
                    onClick={handleSendAdHocNotification}
                    disabled={
                        (!sendSms && !sendEmail) || !selectedStatuses.length
                    }
                >
                    Send message
                </Button>
            </CardFooter>
        </Card>
    );
};

export default AdHocMessengerManager;
